"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type DialogPage = {
  text: string;
  speaker?: string;
  spriteKey?: string;
  choices?: Array<{ id: string; label: string }>;
};

type Props = {
  pages: DialogPage[];
  open: boolean;
  onChoice?: (choiceId: string) => void;
  onComplete: () => void;
  typewriterMs?: number;
};

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const WALNUT = "#5c4a30";
const SAND = "#b89968";

type SurggiesAudio = {
  playSfx?: (name: string) => void;
};

function playSfx(name: string) {
  if (typeof window === "undefined") return;
  const audio = (window as unknown as { surggiesAudio?: SurggiesAudio })
    .surggiesAudio;
  audio?.playSfx?.(name);
}

export function DialogBox({
  pages,
  open,
  onChoice,
  onComplete,
  typewriterMs = 25,
}: Props) {
  const [pageIndex, setPageIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [choiceIndex, setChoiceIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [slideIn, setSlideIn] = useState(false);

  const completedRef = useRef(false);
  // Track previous charCount to fire SFX every 3 chars.
  const prevCharRef = useRef(0);

  const currentPage: DialogPage | undefined = pages[pageIndex];
  const fullText = currentPage?.text ?? "";
  const isLastPage = pageIndex >= pages.length - 1;
  const isTyping = charCount < fullText.length;
  const hasChoices = !!(
    isLastPage &&
    currentPage?.choices &&
    currentPage.choices.length > 0
  );
  const choicesReady = hasChoices && !isTyping;

  // Mount/unmount with slide animation.
  useEffect(() => {
    if (open) {
      completedRef.current = false;
      setMounted(true);
      setPageIndex(0);
      setCharCount(0);
      setChoiceIndex(0);
      prevCharRef.current = 0;
      // Trigger slide-in on next frame.
      const raf = requestAnimationFrame(() => setSlideIn(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setSlideIn(false);
      const t = setTimeout(() => setMounted(false), 220);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Reset char counter when page changes.
  useEffect(() => {
    setCharCount(0);
    setChoiceIndex(0);
    prevCharRef.current = 0;
  }, [pageIndex]);

  // Typewriter tick — fire dialog_tick SFX every 3 characters.
  useEffect(() => {
    if (!open || !mounted) return;
    if (charCount >= fullText.length) return;
    const id = setTimeout(() => {
      const next = Math.min(charCount + 1, fullText.length);
      setCharCount(next);
      // Play tick every 3 chars.
      if (Math.floor(next / 3) > Math.floor(prevCharRef.current / 3)) {
        playSfx("dialog_tick");
      }
      prevCharRef.current = next;
    }, typewriterMs);
    return () => clearTimeout(id);
  }, [open, mounted, charCount, fullText, typewriterMs]);

  const advance = useCallback(() => {
    if (completedRef.current) return;
    if (isTyping) {
      // Snap to end of current page.
      setCharCount(fullText.length);
      return;
    }
    if (hasChoices) {
      // Final page with choices: do not auto-advance; commit handled by selectChoice.
      return;
    }
    playSfx("menu_select");
    if (isLastPage) {
      completedRef.current = true;
      onComplete();
    } else {
      setPageIndex((i) => i + 1);
    }
  }, [isTyping, fullText.length, hasChoices, isLastPage, onComplete]);

  const skipTyping = useCallback(() => {
    if (isTyping) setCharCount(fullText.length);
  }, [isTyping, fullText.length]);

  const selectChoice = useCallback(() => {
    if (!choicesReady || !currentPage?.choices) return;
    const picked = currentPage.choices[choiceIndex];
    if (!picked) return;
    playSfx("menu_select");
    if (completedRef.current) return;
    completedRef.current = true;
    onChoice?.(picked.id);
    onComplete();
  }, [choicesReady, currentPage, choiceIndex, onChoice, onComplete]);

  // Keyboard handling.
  useEffect(() => {
    if (!open || !mounted) return;
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      if (k === "z" || k === "Z" || k === "Enter") {
        e.preventDefault();
        if (choicesReady) {
          selectChoice();
        } else {
          advance();
        }
      } else if (k === "x" || k === "X" || k === "Escape") {
        e.preventDefault();
        skipTyping();
      } else if (choicesReady && (k === "ArrowUp" || k === "ArrowDown")) {
        e.preventDefault();
        const len = currentPage?.choices?.length ?? 0;
        if (len > 0) {
          playSfx("menu_move");
          setChoiceIndex((i) =>
            k === "ArrowUp" ? (i - 1 + len) % len : (i + 1) % len
          );
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    open,
    mounted,
    advance,
    skipTyping,
    selectChoice,
    choicesReady,
    currentPage,
  ]);

  const displayedText = useMemo(
    () => fullText.slice(0, charCount),
    [fullText, charCount]
  );

  if (!mounted) return null;

  const portraitSrc = currentPage?.spriteKey
    ? `/assets/surggies/${currentPage.spriteKey}.png`
    : null;

  // Subtle paper texture using a layered radial-gradient noise.
  const paperTexture =
    "repeating-linear-gradient(0deg, rgba(92,74,48,0.05) 0 1px, transparent 1px 3px), " +
    "repeating-linear-gradient(90deg, rgba(92,74,48,0.04) 0 1px, transparent 1px 4px)";

  // Name badge height used to offset the dialog box position so the badge
  // sits flush above it (approximately 26px for the badge + gap).
  const BADGE_HEIGHT = 26;

  return (
    <>
      <style>{`
        @keyframes dialogbox-arrow-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(4px); }
        }
        .dialogbox-arrow {
          animation: dialogbox-arrow-bob 0.6s ease-in-out infinite;
        }
      `}</style>
      <div
        aria-live="polite"
        role="dialog"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          // Extra top space so the name badge doesn't get clipped.
          height: `calc(30vh + ${BADGE_HEIGHT}px)`,
          minHeight: 180 + BADGE_HEIGHT,
          pointerEvents: "auto",
          transform: slideIn ? "translateY(0)" : "translateY(100%)",
          transition: "transform 200ms ease-out",
          zIndex: 60,
          imageRendering: "pixelated",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        {/* Speaker name badge — bottom of badge aligns with top of dialog panel */}
        {currentPage?.speaker && (
          <div
            style={{
              position: "absolute",
              bottom: `calc(100% - ${BADGE_HEIGHT}px)`,
              left: 16,
              backgroundColor: INK,
              color: CREAM,
              border: `2px solid ${SAND}`,
              padding: "4px 8px",
              fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1.4,
              // Align bottom of badge to top of the panel below.
              // The panel starts at BADGE_HEIGHT from the bottom of the outer div.
              transform: `translateY(${BADGE_HEIGHT}px)`,
            }}
          >
            {currentPage.speaker}
          </div>
        )}

        {/* Main panel — occupies the lower 30vh portion */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: "30vh",
            minHeight: 180,
            backgroundColor: CREAM,
            backgroundImage: paperTexture,
            border: `2px solid ${INK}`,
            boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
            display: "flex",
            alignItems: "stretch",
            padding: "14px 16px",
            gap: 14,
          }}
        >
          {/* Portrait — 48×48, gold border, pixelated */}
          {portraitSrc && (
            <div
              style={{
                width: 48,
                height: 48,
                flex: "0 0 48px",
                border: `2px solid ${SAND}`,
                backgroundColor: INK,
                imageRendering: "pixelated",
                overflow: "hidden",
                alignSelf: "flex-start",
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={portraitSrc}
                alt={currentPage?.speaker ?? "speaker portrait"}
                width={48}
                height={48}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  imageRendering: "pixelated",
                  display: "block",
                }}
                draggable={false}
              />
            </div>
          )}

          {/* Text + choices */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              color: INK,
              position: "relative",
            }}
          >
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                lineHeight: 1.45,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                flex: 1,
              }}
            >
              {displayedText}
            </div>

            {choicesReady && currentPage?.choices && (
              <ul
                role="menu"
                style={{
                  listStyle: "none",
                  margin: "10px 0 0 0",
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {currentPage.choices.map((c, i) => {
                  const selected = i === choiceIndex;
                  return (
                    <li
                      key={c.id}
                      role="menuitem"
                      aria-selected={selected}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "2px 8px",
                        border: `1px solid ${selected ? SAND : "transparent"}`,
                        backgroundColor: "transparent",
                        color: selected ? SAND : INK,
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        cursor: "pointer",
                      }}
                      onMouseEnter={() => {
                        if (!selected) {
                          playSfx("menu_move");
                          setChoiceIndex(i);
                        }
                      }}
                      onClick={() => {
                        setChoiceIndex(i);
                        selectChoice();
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: "1ch",
                          display: "inline-block",
                        }}
                      >
                        {selected ? ">" : " "}
                      </span>
                      <span>{c.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Continue arrow — bobs when full text shown and no choices */}
            {!isTyping && !choicesReady && (
              <div
                className="dialogbox-arrow"
                aria-hidden
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  fontSize: 14,
                  color: SAND,
                  lineHeight: 1,
                  userSelect: "none",
                }}
              >
                {"▼"}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DialogBox;
